import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

export interface PickerOption { id: number; label: string; sublabel?: string }

/**
 * Generic searchable id picker. Shows a human label, commits the id (+ label)
 * on selection. Free-typed text never commits — only a pick (or revert-on-blur)
 * does, so the bound id always matches a real record. Same combobox mechanics as
 * SupplierPicker (preventDefault-on-mousedown so picking doesn't blur/revert).
 */
export function EntityPicker({ queryKey, search, valueLabel, onChange, placeholder, inputClassName, minChars = 2 }: {
  queryKey: string;
  search: (q: string, signal?: AbortSignal) => Promise<PickerOption[]>;
  valueLabel: string;
  onChange: (id: number | null, label: string) => void;
  placeholder?: string;
  inputClassName?: string;
  minChars?: number;
}) {
  const [query, setQuery] = useState(valueLabel);
  const [debounced, setDebounced] = useState(valueLabel);
  const [open, setOpen] = useState(false);

  useEffect(() => { setQuery(valueLabel); }, [valueLabel]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const results = useQuery({
    queryKey: [queryKey, debounced],
    queryFn: ({ signal }) => search(debounced, signal),
    enabled: open && debounced.length >= minChars,
    retry: false,
  });

  const commit = (o: PickerOption) => { onChange(o.id, o.label); setQuery(o.label); setOpen(false); };
  const revert = () => setQuery(valueLabel);
  const rows = results.data ?? [];
  const showMenu = open && debounced.length >= minChars;

  return (
    <div className="relative">
      <input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { setOpen(false); revert(); }}
        placeholder={placeholder}
        aria-label={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={showMenu}
        className={inputClassName}
      />
      {showMenu && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded border border-border bg-white text-sm shadow-md">
          {results.isLoading && <li className="px-3 py-2 text-darkGrey/60">Searching…</li>}
          {results.isError && <li className="px-3 py-2 text-red">Lookup unavailable.</li>}
          {!results.isLoading && !results.isError && rows.length === 0 && (
            <li className="px-3 py-2 text-darkGrey/60">No matches for “{debounced}”.</li>
          )}
          {rows.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(o)}
                className="block w-full px-3 py-1.5 text-left hover:bg-bg"
              >
                <span className="font-medium text-navy">{o.label}</span>
                {o.sublabel && <span className="ml-1.5 text-xs text-darkGrey/50">{o.sublabel}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
